import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import { getEdgeBaseUrl } from "../utils/edge";

const resolveBaseUrl = () => `${getEdgeBaseUrl()}/v1/passkey`;

const buildApi = (sessionToken) => {
  const base = resolveBaseUrl();
  return async (path, init = {}) => {
    const res = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionToken}`,
        ...(init.headers || {}),
      },
    });
    const body = await res.json().catch(() => ({}));
    return { status: res.status, body };
  };
};

const featureAllowed = (feature) => {
  try {
    if (typeof document === "undefined") return true;
    if (document.featurePolicy?.allowsFeature) {
      return document.featurePolicy.allowsFeature(feature);
    }
    if (document.permissionsPolicy?.allowsFeature) {
      return document.permissionsPolicy.allowsFeature(feature);
    }
  } catch {
    // ignore — fall through to "assume allowed"
  }
  return true;
};

export const passkeysSupported = () => {
  if (typeof window === "undefined") return false;
  if (!window.PublicKeyCredential) return false;
  // In a cross-origin iframe these are disabled unless the parent sets
  // allow="publickey-credentials-create; publickey-credentials-get" on the
  // iframe element. If either is blocked, skip the whole feature silently.
  if (!featureAllowed("publickey-credentials-create")) return false;
  if (!featureAllowed("publickey-credentials-get")) return false;
  return true;
};

export const platformAuthenticatorAvailable = async () => {
  if (!passkeysSupported()) return false;
  try {
    const available =
      await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch (err) {
    console.warn("[passkey] platform-authenticator check failed:", err?.name, err?.message);
    return false;
  }
};

/**
 * WebAuthn `create()` requires the calling document to be same-origin with
 * every ancestor frame. Safari enforces this strictly with no escape hatch;
 * Chrome 130+ matches that behavior. Permissions Policy can NOT delegate it
 * for create (only for get). When a publisher embeds our journey from a
 * different subdomain (e.g. `demo.dcams.app` parent → `vx-fe.dcams.app`
 * iframe), registration ceremonies will throw NotAllowedError
 * ("The origin of the document is not the same as its ancestors").
 *
 * Returns true when registration is safe to attempt, false when it would
 * be blocked. Use this to gate the create prompt — for use/auth flows the
 * existing passkeysSupported() check (which covers the `get` permission)
 * is sufficient.
 */
export const isSameOriginWithAncestors = () => {
  if (typeof window === "undefined") return true;
  if (window === window.top) return true;
  try {
    if (location.ancestorOrigins) {
      for (let i = 0; i < location.ancestorOrigins.length; i++) {
        if (location.ancestorOrigins[i] !== location.origin) return false;
      }
      return true;
    }
    // Fallback for browsers without ancestorOrigins (Firefox). Reading
    // top.location.origin throws cross-origin → we treat the throw as
    // "definitely cross-origin".
    return window.top.location.origin === location.origin;
  } catch {
    return false;
  }
};

/**
 * Fire-and-forget tracking event for the worker's analytics pipeline.
 * Used for OFFERED (prompt rendered) and DISMISSED (user skipped/closed).
 * SELECTED and REGISTERED are written server-side from the ceremony calls
 * — the frontend does not need to send those.
 *
 * Posts to the generic `/funnel` endpoint with the event type in the JSON
 * body rather than the URL path, so ad-blockers and annoyance lists can't
 * pattern-match a specific event verb.
 */
export const recordPasskeyEvent = (
  eventType,
  { sessionToken, request_id, workspace, email, visitor_id, metadata },
) => {
  if (!sessionToken || !request_id || !workspace) return;
  const base = resolveBaseUrl();
  const url = `${base}/funnel`;

  // Carry the auth in the body — required because navigator.sendBeacon (the
  // only Web API that guarantees delivery across SPA component teardown)
  // does not support custom headers, including Authorization. The fetch
  // fallback below also sends it in the body for parity. Backend needs to
  // accept session_token from either the body or the Bearer header.
  const payload = JSON.stringify({
    event: eventType,
    session_token: sessionToken,
    request_id,
    workspace,
    email,
    visitor_id,
    metadata,
  });

  // Primary path: sendBeacon. The browser commits this to the network
  // stack before returning, so it survives the component unmount that
  // immediately follows in the dismiss handlers. Returns false if the
  // user-agent queue is full or the call was blocked.
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      const queued = navigator.sendBeacon(url, blob);
      if (queued) return;
    } catch {
      // fall through to fetch
    }
  }

  // Fallback for environments without sendBeacon: keepalive fetch with
  // both header- and body-based auth so either backend mode accepts it.
  fetch(url, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: payload,
  }).catch((err) => {
    console.warn(`[passkey] funnel ${eventType} failed:`, err?.message);
  });
};

export const shouldPrompt = async ({
  sessionToken,
  request_id,
  workspace,
  email,
  visitor_id,
}) => {
  const api = buildApi(sessionToken);
  const params = { request_id, workspace, email, visitor_id };
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== ""),
  );
  const { status, body } = await api(`/should-prompt?${qs}`);
  if (status !== 200) return { prompt: false, reason: "disabled" };
  return body;
};

export const tryPasskeyBypass = async ({
  sessionToken,
  request_id,
  workspace,
  email,
  visitor_id,
}) => {
  const api = buildApi(sessionToken);

  console.log("[passkey] auth: requesting options", { request_id, workspace, email });
  const { status: optStatus, body: options } = await api("/auth/options", {
    method: "POST",
    body: JSON.stringify({ request_id, workspace, email, visitor_id }),
  });
  if (optStatus !== 200) {
    console.warn("[passkey] auth/options failed:", optStatus, options);
    return { bypassed: false };
  }
  console.log("[passkey] auth: got options, rp.id =", options?.rpId || options?.rp?.id, "page origin =", window.location.origin);

  let assertion;
  try {
    assertion = await startAuthentication({ optionsJSON: options });
  } catch (err) {
    // User cancelled, no matching credential, origin/rp.id mismatch, etc. —
    // caller should fall through to the normal verification flow.
    console.error("[passkey] startAuthentication threw:", err);
    console.error("[passkey] error name:", err?.name, "message:", err?.message);
    return { bypassed: false };
  }

  const { status, body } = await api("/auth/verify", {
    method: "POST",
    body: JSON.stringify({ request_id, assertion, visitor_id }),
  });

  if (status === 200 && body?.ok && body?.bypass) {
    console.log("[passkey] auth: bypass succeeded", body);
    return {
      bypassed: true,
      identity: body.identity,
      original_request_id: body.original_request_id,
      credential_id: body.credential_id,
    };
  }
  console.warn("[passkey] auth/verify did not bypass:", status, body);
  return { bypassed: false };
};

export const registerPasskey = async ({
  sessionToken,
  request_id,
  workspace,
  email,
  binding,
  visitor_id,
}) => {
  const api = buildApi(sessionToken);

  console.log("[passkey] register: requesting options", { request_id, workspace, email });
  const { status: optStatus, body: options } = await api("/register/options", {
    method: "POST",
    body: JSON.stringify({
      request_id,
      workspace,
      email,
      binding: binding ?? {},
    }),
  });
  if (optStatus !== 200) {
    console.warn("[passkey] register/options failed:", optStatus, options);
    return { registered: false };
  }
  console.log("[passkey] register: got options, rp.id =", options?.rp?.id, "page origin =", window.location.origin);

  let attestation;
  try {
    attestation = await startRegistration({ optionsJSON: options });
  } catch (err) {
    // NotAllowedError (origin / rp.id mismatch, user cancel), security error,
    // or unsupported device. Surface in console so origin issues are obvious.
    console.error("[passkey] startRegistration threw:", err);
    console.error("[passkey] error name:", err?.name, "message:", err?.message);
    return { registered: false };
  }

  console.log("[passkey] register: got attestation, posting to /register/verify");
  const { status, body } = await api("/register/verify", {
    method: "POST",
    body: JSON.stringify({ request_id, attestation, visitor_id }),
  });
  if (status === 200) {
    console.log("[passkey] register: success", body);
    return {
      registered: true,
      credential_id: body?.credential_id,
      expires_at: body?.expires_at ?? null,
    };
  }
  console.warn("[passkey] register/verify failed:", status, body);
  return { registered: false };
};
